<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :zoneFilterShow="zone_agency_filter"
          :agencyFilterShow="zone_agency_filter"
          :locationFilterShow="true",
          :roleFilterShow="true",
          :processFilterShow="true",
          :workerFilterShow="true",
          :observerFilterShow="true",
          :dateRangeFilterShow="true",
          :processTypeFilterShow="true",
          @changeFilter="updateFilter"
        )
        
  b-row(v-if="isLoading || isLoadingRisks")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          color="#498ceb"
        )

  div(v-else)
    b-row(v-if="!confirmations_total")
      b-col
        b-card(class="text-center")
          h5(class="mb-0")
            | {{ $t('message.no_confirmations_found') }}

    div(v-else)
      b-row
        b-col(sm="3")
          statistic-card-vertical-total(:statistic="confirmations_total")
        b-col(sm="3")
          statistic-card-vertical-index(:statistic="adherence_index")
      
      b-row
        b-col(cols="12")
          bar-chart-process(
            v-if="adherenceByProcess.labels && adherenceByProcess.labels.length",
            :process-data="adherenceByProcess"
          )
      
      b-row
        b-col(lg="6")
          bar-chart-worker-top(
            v-if="workerTopAdherence.labels && workerTopAdherence.labels.length",
            :worker-top-data="workerTopAdherence"
          )
        b-col(lg="6")
          bar-chart-worker-bottom(
            v-if="workerBottomAdherence.labels && workerBottomAdherence.labels.length",
            :worker-bottom-data="workerBottomAdherence"
          )

      b-row
        b-col(md="6")
          bubble-chart-process(
            v-if="inherentRiskData && inherentRiskData.length",
            :risk-data="inherentRiskData"
            title-variable="inherentRiskMap"
          )
        b-col(md="6")
          bubble-chart-process(
            v-if="residualRiskData && residualRiskData.length",
            :risk-data="residualRiskData"
            title-variable="residualRiskMap"
          )

      //- b-card
        b-table-simple(hover, small, caption-top, responsive)
          b-thead(head-variant="light")
            b-tr
              th {{ $t('message.tableHeader.date') }}
              th {{ $t('message.tableHeader.worker') }}
              th {{ $t('message.tableHeader.observer') }}
              th {{ $t('message.tableHeader.location') }}
              th {{ $t('message.tableHeader.process') }}
              th {{ $t('message.tableHeader.score') }}
              th {{ $t('message.tableHeader.total') }}
              th {{ $t('message.tableHeader.adherence') }}
              th {{ $t('message.tableHeader.action') }}
            b-tr(v-for="(confirmation, index) in confirmations", :key="index")
              b-td {{ confirmation.dateString }}
              b-td {{ confirmation.worker }}
              b-td {{ confirmation.observer }}
              b-td {{ confirmation.location }}
              b-td {{ confirmation.process }}
              b-td {{ confirmation.score }}
              b-td {{ confirmation.total }}
              b-td {{ confirmation.adherenceText() }}
              b-td
                view-button(@clicked="router.push({name: 'habit-confirmation-view', params: {id: confirmation.id}})")
                user-button(@clicked="router.push({name: 'apps-users-view', params: {id: confirmation.workerId}})")
      
      b-card
        b-table(
          :items="confirmations"
          :fields="tableHeaders"
          hover
          responsive
        )
          template(#cell(date)="data")
            span {{ data.item.dateString }}
          template(#cell(adherence)="data")
            span {{ data.item.adherenceText() }}
          template(#cell(actions)="data")
            div(class="flex flex-column")
              view-button(@clicked="router.push({name: 'habit-confirmation-view', params: {id: data.item.id}})")
              //- user-button(@clicked="router.push({name: 'apps-users-view', params: {id: data.item.workerId}})")
      
      b-button(v-if="hasPermission('adherence-processes', 'export')", :disabled="!confirmations.length")
        json-excel(:data="confirmations", :fields="excelFields")
          | {{ $t('download_xlsx') }}
</template>

<script>
import { onMounted, ref, computed, watch } from "@vue/composition-api/dist/vue-composition-api";
import useNotifications from "@/composables/useNotifications";
import { BTable, BTableSimple, BThead, BTr, BTd, BCard } from "bootstrap-vue";
import JsonExcel from "vue-json-excel";
import StatisticCardVerticalTotal from './charts/StatisticCardVerticalTotal.vue'
import StatisticCardVerticalIndex from './charts/StatisticCardVerticalIndex.vue'
import BarChartProcess from './charts/ChartjsBarChartProcess.vue'
import BarChartWorkerTop from './charts/ChartjsHorizontalBarChartWorkerTop.vue'
import BarChartWorkerBottom from './charts/ChartjsHorizontalBarChartWorkerBottom.vue'
import BubbleChartProcess from './charts/ChartjsBubbleChartProcess.vue'
import Filters from "@/views/organization/Filters.vue";
import i18n from '@/libs/i18n'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import UserButton from '@/views/components/Shared/Buttons/UserButton.vue'
import { useRouter } from '@core/utils/utils'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import store from "@/store";
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { probabilityValues, impactValues } from '@/constants'
import { oid, date } from '@/helpers/mongoHelpers'
import { hasPermission, permissionScope } from '@/composables/usePermission'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    JsonExcel,
    StatisticCardVerticalTotal,
    StatisticCardVerticalIndex,
    Filters,
    ViewButton,
    UserButton,
    BarChartProcess,
    BarChartWorkerTop,
    BarChartWorkerBottom,
    BubbleChartProcess,
    Loading,
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications();
    const { handleError } = useCommon()
    const { getItemsAggregate } = useLambda()
    
    const userData = store.state?.userStore?.userData;
    const userObjectId = oid(userData.worker_id.$oid)
    const userRole = userData.role
    const clientId = userData.role !== "admin" ? userData.client.$oid : null;
    const userLocations = userData.locations?.length ? userData.locations : null;
    const { zone_agency_filter, default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const confirmations = ref([]);
    const confirmations_total = ref(0);
    const adherence_index = ref(0);
    const adherenceByProcess = ref({})
    const workerTopAdherence = ref({})
    const workerBottomAdherence = ref({})
    const locationFilter = ref([]);
    const roleFilter = ref([]);
    const processFilter = computed(() => store.state.filtersStore.processFilter);
    const companyFilter = computed(() => store.state.filtersStore.companyFilter)
    const idFilter = computed(() => store.state.filtersStore.idFilter)
    const workerFilter = ref([]);
    const observerFilter = ref([]);
    const zoneFilter = ref([]);
    const agencyFilter = ref([]);
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const currentDay = now.getDate()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const thirtyDaysBeforeDate = new Date(currentYear, currentMonth, currentDay - 30)
    const dateRangeFilter = default_language === "en"
      ? ref(`${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getFullYear()} to ${currentMonth + 1}-${currentDay}-${currentYear}`)
      : ref(`${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getFullYear()} to ${currentDay}-${currentMonth + 1}-${currentYear}`)
    const excelFields = ref({})
    const { route, router } = useRouter()
    const { processAdherence, getDatesFromRange } = useCommonDashboards()
    const isLoading = ref(true)
    const isLoadingRisks = ref(true)
    const processTypeFilter = ref([])
    const residualRiskData = ref([])
    const inherentRiskData = ref([])
    const quadrantCounters = ref({})

    // Table Headers
    const tableHeaders = computed(() => {
      return [
        {
          key: 'date',
          label: i18n.t('message.tableHeader.date'),
          sortable: true,
        },
        {
          key: 'worker',
          label: i18n.t('message.tableHeader.worker'),
          sortable: true,
        },
        {
          key: 'observer',
          label: i18n.t('label.evaluator'),
          sortable: true,
        },
        // {
        //   key: 'location',
        //   label: i18n.t('message.tableHeader.location'),
        //   sortable: true,
        // },
        {
          key: 'process',
          label: i18n.t('message.tableHeader.process'),
          sortable: true,
        },
        {
          key: 'score',
          label: i18n.t('message.tableHeader.score'),
          sortable: true,
        },
        {
          key: 'total',
          label: i18n.t('message.tableHeader.total'),
          sortable: true,
        },
        {
          key: 'adherence',
          label: i18n.t('message.tableHeader.adherence'),
          sortable: true,
        },
        {
          key: 'actions',
          label: i18n.t('message.tableHeader.actions'),
        },
      ]
    })

    async function listConfirmations() {
      isLoading.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          pending: { $ne: true },
          date: { $gte: date(startFilter), $lt: date(endFilter) },
        }
        if (clientId) initialQuery.client_id = oid(clientId)

        const finalQuery = {}

        if (observerFilter.value.length > 0) {
          finalQuery["supervisor._id"] = { $in: observerFilter.value }
        }

        if (workerFilter.value.length > 0) {
          finalQuery["worker._id"] = { $in: workerFilter.value }
        }

        if (processFilter.value.length > 0) {
          finalQuery["process._id"] = { $in: processFilter.value }
        }

        if (locationFilter.value.length > 0) {
          finalQuery["worker.locations._id"] = { $in: locationFilter.value }
        }

        if (roleFilter.value.length > 0) {
          finalQuery["worker.roles"] = { $in: roleFilter.value.map(e => oid(e)) }
        }

        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` }
            }))
          })
        }

        // Privileges filter
        if (permissionScope('adherence-processes', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { 'process.evaluator': userObjectId },
              ]
            })
          } else {
            conditions.push({
              $or: [
                { 'process.responsibles': userObjectId },
                { 'process.leadResponsibles': userObjectId },
                { 'process.evaluator': userObjectId },
              ]
            })
          }
        }

        if (companyFilter.value.length > 0) {
          conditions.push({ 'process.company': { $in: companyFilter.value.map(e => oid(e)) } })
        }

        if (idFilter.value.length > 0) {
          conditions.push({ 'process.id': { $in: idFilter.value.map(e => oid(e)) } })
        }

        // Armar el match final
        if (conditions.length) {
          finalQuery.$and = conditions
        }

        const pipeline = [
          { $match: initialQuery },
          { $lookup: { from: 'worker', localField: 'worker', foreignField: '_id', as: 'worker', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1, locations: 1, roles: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'process', localField: 'process', foreignField: '_id', as: 'process', pipeline: [ { $project: { name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
          { $addFields: { worker: { $arrayElemAt: ["$worker", 0] }, supervisor: { $arrayElemAt: ["$supervisor", 0] }, process: { $arrayElemAt: ["$process", 0] } } },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" } } },
        ]

        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        parseConfirmationData(items, startFilter, endFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoading.value = false
      }
    }

    // Data for adherence XLSX File
    function parseConfirmationData(items, startFilter) {
      excelFields.value = {
        [i18n.t('message.tableHeader.date')]: "dateString",
        [i18n.t('message.tableHeader.worker')]: "worker",
        [i18n.t('message.tableHeader.observer')]: "observer",
        [i18n.t('message.tableHeader.location')]: "location",
        [i18n.t('message.tableHeader.process')]: "process",
        [i18n.t('message.tableHeader.score')]: "score",
        [i18n.t('message.tableHeader.total')]: "total",
        [i18n.t('message.tableHeader.adherence')]: "adherence",
        [i18n.t('message.tableHeader.duration')]: "duration",
        [i18n.t('message.tableHeader.comments')]: "comments"
      }

      // Change the dates so that it matches the UTC time zone
      const timezoneOffsetHours = startFilter.getTimezoneOffset() / 60

      let confirmationData = []
      let adherenceData = {score: 0, total: 0}
      let processData = {}
      let workerData = {}

      for (const confirmation of items) {
        if (confirmation.total) {
          const date = new Date(confirmation.date)
          date.setHours(date.getHours() + timezoneOffsetHours)
          const processName = confirmation.process?.name || ''
          const processCodeAndName = confirmation.process?.code ? `${confirmation.process.code} - ${processName}` : processName
          const processId = confirmation.process?._id
          const workerName = confirmation.worker?.name
          const workerId = confirmation.worker?._id
          const score = confirmation.score
          const total = confirmation.total
          let comments = ""
          if(confirmation?.activities[confirmation.activities?.length - 1]){
            for (let comentario in confirmation.activities[confirmation.activities.length - 1]?.behaviours){
              if (confirmation.activities[confirmation.activities.length - 1].behaviours[comentario].type === "text"){
                comments = confirmation.activities[confirmation.activities.length - 1].behaviours[comentario].answer
              }
            }
          }
          let payload = {
            date,
            dateString: date.toLocaleDateString(`${default_language || 'en'}-US`),
            worker: workerName,
            observer: confirmation.supervisor?.name,
            location: confirmation.worker?.locations?.map(e => e.location).join(" / "),
            process: processName,
            score: Number(score) % 1 === 0 ? Number(score) : Number(score).toFixed(1),
            total: total,
            adherence: processAdherence(score, total),
            adherenceText: function() {
              if (this.total === 0) return "-"
              return `${this.adherence}%`
            },
            duration: confirmation.duration,
            id: confirmation._id,
            workerId: workerId,
            comments: comments
          }

          // Add metadata fields if applicable
          if (confirmation.metadata) {
            confirmation.metadata.forEach(e => {
              payload[e.name] = e.answer
              if (!excelFields.value.hasOwnProperty(i18n.t(`metadata.${e.name}`))) {
                excelFields.value[i18n.t(`metadata.${e.name}`)] = e.name
              }
            })
          }
          
          confirmationData.push(payload)
          adherenceData.score += score
          adherenceData.total += total

          // Data for bar charts
          if (processCodeAndName) updateObjectData(processData, processCodeAndName, processId, score, total)
          if (workerName) updateObjectData(workerData, workerName, workerId, score, total)
        }
      }
      confirmationData.sort(function (a, b) {
        if (b.score === 0 && a.score === 0) return b.total - a.total
        return a.adherence - b.adherence
      })
      confirmations.value = confirmationData
      confirmations_total.value = confirmationData.length
      excelFields.value[i18n.t('message.tableHeader.id')] = "id"
      adherence_index.value = adherenceData.total === 0
        ? "-"
        : `${processAdherence(adherenceData.score, adherenceData.total)}%`

      // Set charts data
      adherenceByProcess.value = setChartsData(processData, "desc", 100, "process")
      workerTopAdherence.value = setChartsData(workerData, "desc", 10)
      workerBottomAdherence.value = setChartsData(workerData, "asc", 10)
    }

    async function listRisks() {
      isLoadingRisks.value = true

      residualRiskData.value = []
      inherentRiskData.value = []
      quadrantCounters.value = {}

      try {
        const query = {
          deleted: { $ne: true },
        }
        if (clientId) query.client_id = oid(clientId)

        if (processFilter.value.length > 0) {
          query._id = { $in: processFilter.value.map(id => oid(id)) }
        }

        if (companyFilter.value.length > 0) {
          query.company = { $in: companyFilter.value.map(e => oid(e)) }
        }
  
        if (idFilter.value.length > 0) {
          query.id = { $in: idFilter.value.map(e => oid(e)) }
        }

        const conditions = []

        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              $expr: {
                $regexMatch: {
                  input: "$code",
                  regex: `^${codePrefix}`
                }
              }
            }))
          })
        }

        // Privileges filter
        if (permissionScope('adherence-processes', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { evaluator: userObjectId },
              ],
            })
          } else {
            conditions.push({
              $or: [
                { responsibles: userObjectId },
                { leadResponsibles: userObjectId },
                { evaluator: userObjectId },
              ],
            })
          }
        }

        if (conditions.length) {
          query.$and = conditions
        }

        const pipeline = [ { $match: query } ]

        const items = await getItemsAggregate({ collection: 'process', pipeline })

        parseRiskData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.process_list_fetch_error') })
      } finally {
        isLoadingRisks.value = false
      }
    }

    // Función para convertir valores categóricos a continuos con padding. Esto ayuda a distribuir mejor los puntos en el gráfico de burbujas.
    function toContinuous(value, padding = 0.2) {
      return value + padding + Math.random() * (1 - 2 * padding)
    }

    // Lógica para distribuir riesgos uniformemente dentro de cada cuadrante
    const ROWS = 3
    const COLS = 5
    const CELL_FILL = 0.6
    const VERTICAL_OFFSET = 0.08 // 👈 mueve todo hacia abajo

    function getDistributedPosition(baseX, baseY, indexInQuadrant) {
      // Si hay más puntos que celdas, devolver una posición aleatoria dentro del cuadrante
      if (indexInQuadrant >= ROWS * COLS) {
        return {
          x: toContinuous(baseX),
          y: toContinuous(baseY)
        }
      }

      const row = Math.floor(indexInQuadrant / COLS)
      const col = indexInQuadrant % COLS

      const cellWidth = 1 / COLS
      const cellHeight = 1 / ROWS

      const offsetX = (cellWidth * (1 - CELL_FILL)) / 2
      const offsetY = (cellHeight * (1 - CELL_FILL)) / 2

      const x = baseX + col * cellWidth + offsetX + (cellWidth * CELL_FILL) / 2
      let y = baseY + (ROWS - row - 1) * cellHeight + offsetY + (cellHeight * CELL_FILL) / 2
      y -= VERTICAL_OFFSET    // empujar todo el cuadrante hacia abajo

      return { x, y }
    }

    function addRiskToChartData(probability, impact, name, code, isResidual) {
      const probabilityValue = probabilityValues[probability]
      const impactValue = impactValues[impact]

      const quadrantKey = `${impactValue}-${probabilityValue}`

      if (!quadrantCounters.value[quadrantKey]) {
        quadrantCounters.value[quadrantKey] = 0
      }

      const indexInQuadrant = quadrantCounters.value[quadrantKey]
      const { x: probabilityValueAdjusted, y: impactValueAdjusted } = getDistributedPosition(probabilityValue, impactValue, indexInQuadrant)

      quadrantCounters.value[quadrantKey]++

      if (isResidual) {
        residualRiskData.value.push({
          x: probabilityValueAdjusted,
          y: impactValueAdjusted,
          name: name || '',
          code: code || '',
          probability: probability || '',
          impact: impact || '',
        })
      } else {
        inherentRiskData.value.push({
          x: probabilityValueAdjusted,
          y: impactValueAdjusted,
          name: name || '',
          code: code || '',
          probability: probability || '',
          impact: impact || '',
        })
      }
    }

    function parseRiskData(items) {
      for (const risk of items) {
        // Residual risk
        if (risk.residualRiskProbability && risk.residualRiskImpact) {
          addRiskToChartData(risk.residualRiskProbability, risk.residualRiskImpact, risk.name, risk.code, true)
        }

        // Inherent risk
        if (risk.inherentRiskProbability && risk.inherentRiskImpact) {
          addRiskToChartData(risk.inherentRiskProbability, risk.inherentRiskImpact, risk.name, risk.code, false)
        }
      }
    }

    function updateFilter(data) {
      locationFilter.value = data.locationFilter;
      roleFilter.value = data.roleFilter;
      // processFilter.value = data.processFilter; // processFilter ya se actualiza automáticamente desde el store global
      workerFilter.value = data.workerFilter;
      observerFilter.value = data.observerFilter;
      dateRangeFilter.value = data.dateRangeFilter;
      zoneFilter.value = data.zoneFilter;
      agencyFilter.value = data.agencyFilter;
      processTypeFilter.value = data.processTypeFilter

      listConfirmations();
      listRisks()
    }

    watch([companyFilter, idFilter], () => {
      listConfirmations()
      listRisks()
    })

    const updateObjectData = (object, property, propertyId, score, total) => {
      if (object.hasOwnProperty(property)) {
        object[property].score += score
        object[property].total += total
        object[property].confirmations += 1
      }
      else object[property] = {
        score: score,
        total: total,
        confirmations: 1,
        id: propertyId
      }
    }

    const setChartsData = (objectData, sortDirection, dataLimit, category) => {
      for (const property in objectData) {
        objectData[property].adherence = processAdherence(objectData[property].score, objectData[property].total)
      }

      let dataSorted = Object.entries(objectData)

      dataSorted.sort(function (a, b) {
        if (b[1].score === 0 && a[1].score === 0) return sortDirection === "desc" ? a[1].total - b[1].total : b[1].total - a[1].total
        return sortDirection === "desc" ? b[1].adherence - a[1].adherence : a[1].adherence - b[1].adherence
      })

      const limit = dataLimit < dataSorted.length ? dataLimit : dataSorted.length

      let labels = []
      let adherenceValues = []
      let confirmationValues = []
      let idValues = []
      let completeLabels = []

      for (let i = 0; i < limit; i++) {
        labels.push(category === 'process' && dataSorted[i][0].length > 24 ? dataSorted[i][0].slice(0, 24) + "..." : dataSorted[i][0])
        adherenceValues.push(dataSorted[i][1].adherence)
        confirmationValues.push(dataSorted[i][1].confirmations)
        idValues.push(dataSorted[i][1].id)
        completeLabels.push(dataSorted[i][0])
      }

      return {
        labels: labels,
        data: {
          adherence: adherenceValues,
          confirmations: confirmationValues
        },
        additionalInfo: {
          ids: idValues,
          completeLabels,
        }
      }
    }

    onMounted(() => {
      if (route.value.query.processId) {
        store.dispatch('filtersStore/setProcessFilter', [route.value.query.processId])
      } else {
        listConfirmations()
        listRisks()
      }
    })

    return {
      hasPermission,
      updateFilter,
      confirmations,
      confirmations_total,
      adherence_index,
      adherenceByProcess,
      workerTopAdherence,
      workerBottomAdherence,
      excelFields,
      router,
      isLoading,
      isLoadingRisks,
      zone_agency_filter,
      tableHeaders,
      residualRiskData,
      inherentRiskData,
    };
  },
};
</script>
